import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { db } from '../../firebase/config';


// Async thunks
export const fetchShifts = createAsyncThunk(
  'shifts/fetchShifts',
  async ({ date, driverId } = {}, { rejectWithValue }) => {
    try {
      let shiftsQuery;
      
      if (date && driverId) {
        // Get shifts for specific date and driver
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);
        
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);
        
        shiftsQuery = query(
          collection(db, 'shifts'),
          where('driver_id', '==', driverId),
          where('start_time', '>=', startOfDay.toISOString()),
          where('start_time', '<=', endOfDay.toISOString()),
          orderBy('start_time', 'asc')
        );
      } else if (date) {
        // Get shifts for specific date
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);
        
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);
        
        shiftsQuery = query(
          collection(db, 'shifts'),
          where('start_time', '>=', startOfDay.toISOString()),
          where('start_time', '<=', endOfDay.toISOString()),
          orderBy('start_time', 'asc')
        );
      } else if (driverId) {
        // Get shifts for specific driver
        shiftsQuery = query(
          collection(db, 'shifts'),
          where('driver_id', '==', driverId),
          orderBy('start_time', 'desc')
        );
      } else {
        // Get all shifts
        shiftsQuery = query(
          collection(db, 'shifts'),
          orderBy('start_time', 'desc')
        );
      }
      
      const querySnapshot = await getDocs(shiftsQuery);
      const shifts = [];
      
      for (const shiftDoc of querySnapshot.docs) {
        const shiftData = shiftDoc.data();
        
        // Fetch driver data - Fahrer sind direkt in der users-Collection
        let driverData = null;
        
        if (shiftData.driver_id) {
          const userDoc = await getDoc(doc(db, 'users', shiftData.driver_id));
          
          if (userDoc.exists() && userDoc.data().isDriver) {
            driverData = userDoc.data();
          }
        }
        
        // Fetch vehicle data - Collection heißt "Vehicles" (mit großem V)
        let vehicleData = null;
        if (shiftData.vehicle_id) {
          const vehicleDoc = await getDoc(doc(db, 'Vehicles', shiftData.vehicle_id));
          vehicleData = vehicleDoc.exists() ? vehicleDoc.data() : null;
        }
        
        shifts.push({
          id: shiftDoc.id,
          ...shiftData,
          driver: driverData ? {
            id: shiftData.driver_id,
            ...driverData,
            user: {
              id: shiftData.driver_id,
              name: driverData.name,
              email: driverData.email,
              phone: driverData.phone || '',
              profile_picture: driverData.profilePic || '',
            }
          } : null,
          vehicle: vehicleData ? {
            id: shiftData.vehicle_id,
            ...vehicleData
          } : null
        });
      }
      
      return shifts;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchShiftById = createAsyncThunk(
  'shifts/fetchShiftById',
  async (shiftId, { rejectWithValue }) => {
    try {
      const shiftDoc = await getDoc(doc(db, 'shifts', shiftId));
      
      if (!shiftDoc.exists()) {
        throw new Error('Schicht nicht gefunden');
      }
      
      const shiftData = shiftDoc.data();
      
      // Fetch driver data - Fahrer sind direkt in der users-Collection
      let driverData = null;
      
      if (shiftData.driver_id) {
        const userDoc = await getDoc(doc(db, 'users', shiftData.driver_id));
        
        if (userDoc.exists() && userDoc.data().isDriver) {
          driverData = userDoc.data();
        }
      }
      
      // Fetch vehicle data - Collection heißt "Vehicles" (mit großem V)
      let vehicleData = null;
      if (shiftData.vehicle_id) {
        const vehicleDoc = await getDoc(doc(db, 'Vehicles', shiftData.vehicle_id));
        vehicleData = vehicleDoc.exists() ? vehicleDoc.data() : null;
      }
      
      return {
        id: shiftDoc.id,
        ...shiftData,
        driver: driverData ? {
          id: shiftData.driver_id,
          ...driverData,
          user: {
            id: shiftData.driver_id,
            name: driverData.name,
            email: driverData.email,
            phone: driverData.phone || '',
            profile_picture: driverData.profilePic || '',
          }
        } : null,
        vehicle: vehicleData ? {
          id: shiftData.vehicle_id,
          ...vehicleData
        } : null
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createShift = createAsyncThunk(
  'shifts/createShift',
  async (shiftData, { rejectWithValue }) => {
    try {
      const { driver_id, vehicle_id, start_time, end_time, notes } = shiftData;
      
      // Validate that driver exists - Fahrer sind direkt in der users-Collection
      const userDoc = await getDoc(doc(db, 'users', driver_id));
      if (!userDoc.exists() || !userDoc.data().isDriver) {
        throw new Error('Fahrer nicht gefunden');
      }
      const driverData = userDoc.data();
      
      // Validate that vehicle exists - Collection heißt "Vehicles" (mit großem V)
      const vehicleDoc = await getDoc(doc(db, 'Vehicles', vehicle_id));
      if (!vehicleDoc.exists()) {
        throw new Error('Fahrzeug nicht gefunden');
      }
      
      // Check for overlapping shifts for the same driver
      const driverShiftsQuery = query(
        collection(db, 'shifts'),
        where('driver_id', '==', driver_id)
      );
      
      const driverShifts = await getDocs(driverShiftsQuery);
      
      for (const existingShiftDoc of driverShifts.docs) {
        const existingShift = existingShiftDoc.data();
        
        // Check if the new shift overlaps with an existing shift
        if (
          (new Date(start_time) < new Date(existingShift.end_time) && 
           new Date(end_time) > new Date(existingShift.start_time))
        ) {
          throw new Error('Der Fahrer hat bereits eine überlappende Schicht für diesen Zeitraum');
        }
      }
      
      const newShift = {
        driver_id,
        vehicle_id,
        start_time,
        end_time,
        status: 'scheduled',
        total_rides: 0,
        total_earnings: 0,
        break_times: [],
        notes: notes || '',
        created_at: new Date().toISOString()
      };
      
      const docRef = await addDoc(collection(db, 'shifts'), newShift);
      
      // Fahrerinformationen bereits aus userDoc geholt
      
      // Fetch vehicle data
      const vehicleData = vehicleDoc.data();
      
      return {
        id: docRef.id,
        ...newShift,
        driver: {
          id: driver_id,
          ...driverData,
          user: {
            id: driver_id,
            name: driverData.name,
            email: driverData.email,
            phone: driverData.phone || '',
            profile_picture: driverData.profilePic || '',
          }
        },
        vehicle: {
          id: vehicle_id,
          ...vehicleData
        }
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateShift = createAsyncThunk(
  'shifts/updateShift',
  async ({ id, ...shiftData }, { rejectWithValue }) => {
    try {
      // If updating time, check for conflicts
      if (shiftData.start_time || shiftData.end_time) {
        const shiftRef = doc(db, 'shifts', id);
        const shiftDoc = await getDoc(shiftRef);
        
        if (!shiftDoc.exists()) {
          throw new Error('Schicht nicht gefunden');
        }
        
        const currentShift = shiftDoc.data();
        const newStartTime = shiftData.start_time || currentShift.start_time;
        const newEndTime = shiftData.end_time || currentShift.end_time;
        
        // Check for overlapping shifts for the same driver
        const driverShiftsQuery = query(
          collection(db, 'shifts'),
          where('driver_id', '==', currentShift.driver_id)
        );
        
        const driverShifts = await getDocs(driverShiftsQuery);
        
        for (const existingShiftDoc of driverShifts.docs) {
          // Skip the current shift
          if (existingShiftDoc.id === id) continue;
          
          const existingShift = existingShiftDoc.data();
          
          // Check if the updated shift overlaps with an existing shift
          if (
            (new Date(newStartTime) < new Date(existingShift.end_time) && 
             new Date(newEndTime) > new Date(existingShift.start_time))
          ) {
            throw new Error('Der Fahrer hat bereits eine überlappende Schicht für diesen Zeitraum');
          }
        }
      }
      
      const shiftRef = doc(db, 'shifts', id);
      await updateDoc(shiftRef, {
        ...shiftData,
        updated_at: new Date().toISOString()
      });
      
      // Fetch the updated shift
      const updatedShiftDoc = await getDoc(shiftRef);
      const updatedShiftData = updatedShiftDoc.data();
      
      // Fetch driver data - Fahrer sind direkt in der users-Collection
      let driverData = null;
      
      if (updatedShiftData.driver_id) {
        const userDoc = await getDoc(doc(db, 'users', updatedShiftData.driver_id));
        
        if (userDoc.exists() && userDoc.data().isDriver) {
          driverData = userDoc.data();
        }
      }
      
      // Fetch vehicle data - Collection heißt "Vehicles" (mit großem V)
      let vehicleData = null;
      if (updatedShiftData.vehicle_id) {
        const vehicleDoc = await getDoc(doc(db, 'Vehicles', updatedShiftData.vehicle_id));
        vehicleData = vehicleDoc.exists() ? vehicleDoc.data() : null;
      }
      
      return {
        id,
        ...updatedShiftData,
        driver: driverData ? {
          id: updatedShiftData.driver_id,
          ...driverData,
          user: {
            id: updatedShiftData.driver_id,
            name: driverData.name,
            email: driverData.email,
            phone: driverData.phone || '',
            profile_picture: driverData.profilePic || '',
          }
        } : null,
        vehicle: vehicleData ? {
          id: updatedShiftData.vehicle_id,
          ...vehicleData
        } : null
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteShift = createAsyncThunk(
  'shifts/deleteShift',
  async (id, { rejectWithValue }) => {
    try {
      await deleteDoc(doc(db, 'shifts', id));
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addBreakToShift = createAsyncThunk(
  'shifts/addBreak',
  async ({ shiftId, breakData }, { rejectWithValue }) => {
    try {
      const shiftRef = doc(db, 'shifts', shiftId);
      const shiftDoc = await getDoc(shiftRef);
      
      if (!shiftDoc.exists()) {
        throw new Error('Schicht nicht gefunden');
      }
      
      const shiftData = shiftDoc.data();
      const breakTimes = shiftData.break_times || [];
      
      const newBreak = {
        ...breakData,
        id: Date.now().toString()
      };
      
      await updateDoc(shiftRef, {
        break_times: [...breakTimes, newBreak],
        updated_at: new Date().toISOString()
      });
      
      // Fetch updated shift
      const updatedShiftDoc = await getDoc(shiftRef);
      const updatedShiftData = updatedShiftDoc.data();
      
      return {
        id: shiftId,
        ...updatedShiftData
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  shifts: [],
  currentShift: null,
  isLoading: false,
  error: null
};

const shiftsSlice = createSlice({
  name: 'shifts',
  initialState,
  reducers: {
    clearShiftsError: (state) => {
      state.error = null;
    },
    clearCurrentShift: (state) => {
      state.currentShift = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch shifts
      .addCase(fetchShifts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShifts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.shifts = action.payload;
      })
      .addCase(fetchShifts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch shift by ID
      .addCase(fetchShiftById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShiftById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentShift = action.payload;
      })
      .addCase(fetchShiftById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create shift
      .addCase(createShift.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createShift.fulfilled, (state, action) => {
        state.isLoading = false;
        state.shifts.push(action.payload);
        state.currentShift = action.payload;
      })
      .addCase(createShift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update shift
      .addCase(updateShift.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateShift.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.shifts.findIndex(shift => shift.id === action.payload.id);
        if (index !== -1) {
          state.shifts[index] = action.payload;
        }
        if (state.currentShift && state.currentShift.id === action.payload.id) {
          state.currentShift = action.payload;
        }
      })
      .addCase(updateShift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete shift
      .addCase(deleteShift.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteShift.fulfilled, (state, action) => {
        state.isLoading = false;
        state.shifts = state.shifts.filter(shift => shift.id !== action.payload);
        if (state.currentShift && state.currentShift.id === action.payload) {
          state.currentShift = null;
        }
      })
      .addCase(deleteShift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add break to shift
      .addCase(addBreakToShift.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addBreakToShift.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.shifts.findIndex(shift => shift.id === action.payload.id);
        if (index !== -1) {
          state.shifts[index] = {
            ...state.shifts[index],
            ...action.payload
          };
        }
        if (state.currentShift && state.currentShift.id === action.payload.id) {
          state.currentShift = {
            ...state.currentShift,
            ...action.payload
          };
        }
      })
      .addCase(addBreakToShift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const { clearShiftsError, clearCurrentShift } = shiftsSlice.actions;
export default shiftsSlice.reducer;